import {
  ApplicationRef,
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  Injectable,
  inject,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { timer } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { Preferences } from '@capacitor/preferences';
import { SplashScreen } from '@capacitor/splash-screen';
import { Capacitor } from '@capacitor/core';
import { BrandAssetsService } from './brand-assets.service';
import { BrandedSplashOverlayComponent } from './branded-splash-overlay.component';
import { BrandRegistryEntry } from './brands.registry';

export const LAST_BRAND_CODE_STORAGE_KEY = 'lastBrandCode';

// NOTE: Routes that mean "we're still bootstrapping the session" and
// should NOT trigger the auto-fade fallback. Anything else (home,
// error, account create, login UI, etc.) is considered a stable
// destination and is safe to reveal.
const BOOTSTRAP_ROUTE_PREFIXES = ['/auth/authorize'] as const;

// NOTE: Hard timeout for the boot overlay. If after this many ms nobody
// has triggered the fadeOut (no NavigationEnd, no AccountLoaded, no
// error caught), the watchdog forces the overlay down so the user is
// never trapped on an eternal splash.
const SPLASH_WATCHDOG_MS = 12_000;

/**
 * Servicio que orquesta el arranque de la experiencia branded en mobile.
 *
 * Flujo (ejecutado vía provideAppInitializer en mobile-app.module):
 *  1. Monta `BrandedSplashOverlayComponent` sobre `<body>` con estado
 *     `booting` (blanco + logo SideDrawer gris).
 *  2. Llama `SplashScreen.hide()` para ceder el control del splash al
 *     overlay web. Como el overlay es visualmente idéntico al nativo
 *     en este momento, el usuario no percibe la transición.
 *  3. Lee `lastBrandCode` de Preferences y, si existe, resuelve su
 *     entry en el registry y dispara `showBranded(entry)` para
 *     animar el crossfade al brand cacheado.
 *
 * El fadeOut del overlay y el swap real del ícono los dispara
 * `MobileBrandingEffects` cuando llega `BrandSwapRequested` desde
 * `accountLoadedBrandSwap$`.
 */
@Injectable({ providedIn: 'root' })
export class BrandingBootstrapService {
  private readonly appRef = inject(ApplicationRef);
  private readonly injector = inject(EnvironmentInjector);
  private readonly document = inject(DOCUMENT);
  private readonly brandAssets = inject(BrandAssetsService);
  private readonly router = inject(Router);

  private overlayRef: ComponentRef<BrandedSplashOverlayComponent> | null = null;

  getOverlay(): BrandedSplashOverlayComponent | null {
    return this.overlayRef?.instance ?? null;
  }

  async initialize(): Promise<void> {
    this.mountOverlay();

    await this.hideNativeSplash();

    // NOTE: To QA the branded splash overlay without depending on the
    // Preferences cache, uncomment the FORCE_BRAND_CODE block below and set
    // it to a registered brandCode (see brands.registry.ts). Re-comment
    // before shipping.
    // const FORCE_BRAND_CODE: string | null = 'goldenstatewarriors';
    // const cached = FORCE_BRAND_CODE ?? (await this.readCachedBrandCode());
    const cached = await this.readCachedBrandCode();
    if (cached) {
      const entry = this.brandAssets.resolve(cached);
      this.overlayRef?.instance.showBranded(entry);
    }

    this.armNavigationFallback();
    this.armWatchdog();
  }

  async getCachedBrandCode(): Promise<string | null> {
    return this.readCachedBrandCode();
  }

  async persistBrandCode(brandCode: string): Promise<void> {
    try {
      await Preferences.set({
        key: LAST_BRAND_CODE_STORAGE_KEY,
        value: brandCode,
      });
    } catch (err) {
      console.warn('[BrandingBootstrap] persistBrandCode failed', err);
    }
  }

  applyBranded(entry: BrandRegistryEntry): void {
    this.overlayRef?.instance.showBranded(entry);
  }

  async fadeOutOverlay(afterMs: number = 300): Promise<void> {
    await this.overlayRef?.instance.fadeOut(afterMs);
  }

  private mountOverlay(): void {
    if (this.overlayRef) {
      return;
    }
    const hostElement = this.document.createElement('div');
    hostElement.setAttribute('data-branded-splash-host', '');
    this.document.body.appendChild(hostElement);

    this.overlayRef = createComponent(BrandedSplashOverlayComponent, {
      environmentInjector: this.injector,
      hostElement,
    });
    this.overlayRef.instance.showBooting();
    this.appRef.attachView(this.overlayRef.hostView);
  }

  private async hideNativeSplash(): Promise<void> {
    if (!Capacitor.isNativePlatform()) {
      return;
    }
    try {
      await SplashScreen.hide();
    } catch (err) {
      console.warn('[BrandingBootstrap] SplashScreen.hide failed', err);
    }
  }

  private async readCachedBrandCode(): Promise<string | null> {
    try {
      const { value } = await Preferences.get({
        key: LAST_BRAND_CODE_STORAGE_KEY,
      });
      return value ?? null;
    } catch (err) {
      console.warn('[BrandingBootstrap] readCachedBrandCode failed', err);
      return null;
    }
  }

  /**
   * Master safety net based on Router events.
   *
   * As soon as the app lands on the first stable route (anything that
   * is NOT an auth callback), the overlay is faded out. This covers
   * paths the explicit hooks in effects don't reach: unhandled errors,
   * unauthenticated first launch landing on login UI, tenant config
   * failures, etc.
   *
   * `fadeOut()` is idempotent. If `MobileBrandingEffects` already
   * triggered its own fadeOut, this becomes a no-op.
   */
  private armNavigationFallback(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        filter(event => !this.isBootstrapRoute(event.urlAfterRedirects)),
        take(1)
      )
      .subscribe(() => {
        void this.fadeOutOverlay(0);
      });
  }

  private isBootstrapRoute(url: string): boolean {
    return BOOTSTRAP_ROUTE_PREFIXES.some(prefix => url.startsWith(prefix));
  }

  /**
   * Final safety net. If after `SPLASH_WATCHDOG_MS` the overlay is
   * still visible (no NavigationEnd, no AccountLoaded, no error path
   * fired), force the fadeOut so the user is never stuck on an
   * eternal splash.
   *
   * The console.warn is intentional: if it ever fires in production
   * it means another layer (auth, tenant, routing) is silently
   * failing and should be investigated.
   */
  private armWatchdog(): void {
    timer(SPLASH_WATCHDOG_MS)
      .pipe(take(1))
      .subscribe(() => {
        const overlay = this.overlayRef?.instance;
        if (!overlay || overlay.state() === 'hidden') {
          return;
        }
        console.warn(
          '[BrandingBootstrap] Watchdog fired: forcing overlay fadeOut after',
          SPLASH_WATCHDOG_MS,
          'ms'
        );
        void this.fadeOutOverlay(0);
      });
  }
}
